import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import ShadowContainer from "../../components/common/ShadowContainer";
import api from "../../utils/api";
import { API_BASE } from "../../utils/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const token = localStorage.getItem("token");

const validationSchema = Yup.object({
  name: Yup.object({
    en: Yup.string().required("Category name is required"),
  }),
  description: Yup.object({
    en: Yup.string().required("Description is required"),
  }),
  image: Yup.mixed()
    .required("Image is required")
    .test(
      "fileSize",
      "File size must be 5MB or less",
      (value) => !value || value.size <= 5 * 1024 * 1024,
    )
    .test(
      "fileType",
      "Only PNG and JPEG images are allowed",
      (value) => !value || ["image/png", "image/jpeg"].includes(value.type),
    ),
});

const CategoryAdd = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [enableMultiLang, setEnableMultiLang] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");
  const [printerCategories, setPrinterCategories] = useState([]);

  const excludedWords = ["is", "of", "the", "and", "or", "in", "to", "with"];

  useEffect(() => {
    fetch(`${API_BASE}/api/languages/availableLanguages`)
      .then((res) => res.json())
      .then((data) => setLanguages(data.filter((l) => l.code !== "en")))
      .catch(() => toast.error("Failed to load languages"));
  }, []);
  useEffect(() => {
    if (!ENABLE_PRINT_CAT) return;

    fetch(`${API_BASE}/api/restaurant/loadPrintCatagories`)
      .then((res) => res.json())
      .then((data) => setPrinterCategories(data))
      .catch(() => toast.error("Failed to load printer categories"));
  }, []);

  const ENABLE_PRINT_CAT = process.env.REACT_APP_PRINT_CAT === "true";

  const capitalizeWords = (str) =>
    str
      .split(" ")
      .map((word) =>
        excludedWords.includes(word.toLowerCase())
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(" ");

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const nameObj = {};
      const descObj = {};

      Object.keys(values.name).forEach((lang) => {
        if (values.name[lang]) {
          nameObj[lang] = values.name[lang];
          descObj[lang] = values.description[lang];
        }
      });

      const formData = new FormData();
      formData.append("name", JSON.stringify(nameObj));
      formData.append("description", JSON.stringify(descObj));
      formData.append("slug", generateSlug(values.name.en));
      formData.append("image", values.image);
      formData.append("printKey", values.printer || "kitchen");

      const response = await api.post("/categories", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        toast.success("Category added successfully");
        resetForm();
        setEnableMultiLang(false);
        setSelectedLang("");
        setImagePreview(null);
        setFileInputKey(Date.now());
      } else {
        toast.error("Error adding category");
      }
    } catch (err) {
      toast.error("Failed to add category");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3">
      <PageHeader
        showBackButton={true}
        backTo="/categories"
        title="Add Category"
        subtitle="Create a new category for your menu"
      />

      <ShadowContainer maxWidth="max-w-4xl">
        <Formik
          initialValues={{
            name: { en: "" },
            description: { en: "" },
            image: null,
            printer: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            setFieldValue,
            handleBlur,
            values,
            touched,
            errors,
            isSubmitting,
          }) => (
            <Form className="space-y-6">
              {/* Category Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={values.name.en}
                  onBlur={handleBlur}
                  onChange={(e) =>
                    setFieldValue("name.en", capitalizeWords(e.target.value))
                  }
                  disabled={isSubmitting}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors ${
                    touched.name?.en && errors.name?.en
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                  } ${isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  placeholder="Enter category name"
                />
                {touched.name?.en && errors.name?.en && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.en}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={values.description.en}
                  onBlur={handleBlur}
                  onChange={(e) =>
                    setFieldValue("description.en", e.target.value)
                  }
                  disabled={isSubmitting}
                  rows={3}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none ${
                    touched.description?.en && errors.description?.en
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                  } ${isSubmitting ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  placeholder="Enter description"
                />
                {touched.description?.en && errors.description?.en && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.description.en}
                  </p>
                )}
              </div>

              {/* Printer Category */}
              {ENABLE_PRINT_CAT && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Printer (Optional)
                  </label>
                  <select
                    value={values.printer}
                    onChange={(e) => setFieldValue("printer", e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                  >
                    <option value="">Select printer</option>
                    {printerCategories.map((pc) => (
                      <option key={pc.key} value={pc.key}>
                        {pc.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Enable Multi-language */}
              {languages && languages.length > 0 && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableMultiLang"
                    checked={enableMultiLang}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableMultiLang(checked);
                      if (!checked) {
                        setSelectedLang("");
                        setFieldValue("name", { en: values.name.en });
                        setFieldValue("description", {
                          en: values.description.en,
                        });
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="enableMultiLang"
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    Enable multi-language
                  </label>
                </div>
              )}

              {/* Language Selection */}
              {enableMultiLang && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Select Language
                  </p>
                  <div className="space-y-2">
                    {languages.map((lang) => (
                      <label
                        key={lang.code}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="language"
                          value={lang.code}
                          checked={selectedLang === lang.code}
                          onChange={(e) => {
                            const langCode = e.target.value;
                            setSelectedLang(langCode);

                            if (!values.name[langCode]) {
                              setFieldValue(`name.${langCode}`, "");
                              setFieldValue(`description.${langCode}`, "");
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {lang.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Multi-language Fields */}
              {enableMultiLang && selectedLang && (
                <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Name ({selectedLang.toUpperCase()})
                    </label>
                    <input
                      type="text"
                      value={values.name[selectedLang] || ""}
                      onChange={(e) =>
                        setFieldValue(`name.${selectedLang}`, e.target.value)
                      }
                      dir={
                        languages.find((l) => l.code === selectedLang)
                          ?.direction || "ltr"
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                      placeholder={`Enter category name in ${selectedLang}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description ({selectedLang.toUpperCase()})
                    </label>
                    <textarea
                      value={values.description[selectedLang] || ""}
                      onChange={(e) =>
                        setFieldValue(
                          `description.${selectedLang}`,
                          e.target.value,
                        )
                      }
                      dir={
                        languages.find((l) => l.code === selectedLang)
                          ?.direction || "ltr"
                      }
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none bg-white"
                      placeholder={`Enter description in ${selectedLang}`}
                    />
                  </div>
                </div>
              )}

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Image <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    key={fileInputKey}
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFieldValue("image", file);
                        setImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    PNG or JPEG (max 5MB)
                  </p>
                  {touched.image && errors.image && (
                    <p className="mt-1 text-sm text-red-600">{errors.image}</p>
                  )}
                </div>
                {imagePreview && (
                  <div className="mt-4">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-40 h-40 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2.5 rounded-lg font-medium text-white transition-colors duration-200 ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    "Save Category"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </ShadowContainer>
    </div>
  );
};

export default CategoryAdd;
